import { FileText, Printer } from 'lucide-react';
import { useRef } from 'react';
import { Spinner } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useCreditPack } from '@/hooks/use-credit-pack';
import { printHtmlPreview } from '@/hooks/use-pdf-export';
import { fmtDate, fmtDateOnly } from '@/lib/format';

function fmt(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(value);
}

interface Props {
  loanId: string;
  loanNumber: string;
  open: boolean;
  onClose: () => void;
}

export function CreditPackModal({ loanId, loanNumber, open, onClose }: Props) {
  const { data: pack, isLoading } = useCreditPack(loanId, open);
  const printRef = useRef<HTMLDivElement>(null);

  function handlePrint() {
    if (!printRef.current) return;
    printHtmlPreview(`
      <html>
        <head>
          <title>Credit Pack — ${loanNumber}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111; padding: 24px; font-size: 11px; line-height: 1.5; }
            h1 { font-size: 18px; margin-bottom: 4px; }
            h2 { font-size: 14px; margin-top: 20px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
            h3 { font-size: 12px; margin-top: 12px; margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
            th, td { border: 1px solid #ddd; padding: 4px 8px; text-align: left; font-size: 10px; }
            th { background: #f5f5f5; font-weight: 600; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
            .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
            .field { }
            .field-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
            .field-value { font-size: 11px; font-weight: 600; }
            .highlight { background: #f0f0ff; padding: 8px; border-radius: 4px; }
            .warn { background: #fff8e6; padding: 8px; border-radius: 4px; }
            .risk-high { color: #dc2626; }
            .risk-medium { color: #d97706; }
            .risk-low { color: #16a34a; }
            .footer { margin-top: 24px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9px; color: #999; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>${printRef.current.innerHTML}
          <div class="footer">
            Generated by Loan Manager — ${new Date().toLocaleString('en-AU')} — CONFIDENTIAL
          </div>
        </body>
      </html>
    `);
  }

  if (!open) return null;

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-indigo-600" />
          <span>Credit Approval Pack — {loanNumber}</span>
        </div>
      }
      headerAction={
        <span onClick={handlePrint} className="hover:opacity-80 cursor-pointer">
          <Printer className="w-5 h-5 min-w-5 min-h-5" />
        </span>
      }
      classes={{ title: 'w-[96%]' }}
    >
      <>
        {isLoading && (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
            Generating credit pack…
          </div>
        )}

        {pack && (
          <div ref={printRef} className="p-6 space-y-6 text-sm text-gray-800">
            {/* Header */}
            <div>
              <h1 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '2px' }}>
                Loan Manager — Credit Approval Pack
              </h1>
              <p style={{ fontSize: '11px', color: '#666' }}>
                Loan {pack.loan.loanNumber} · Generated {fmtDate(pack.generatedAt)} · CONFIDENTIAL
              </p>
            </div>

            {/* Loan Summary */}
            <div>
              <h2
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '4px',
                }}
              >
                1. Loan Summary
              </h2>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <Field label="Loan Number" value={pack.loan.loanNumber} />
                <Field label="Status" value={pack.loan.status} />
                <Field label="Type" value={pack.loan.loanType} />
                <Field label="Principal" value={fmt(pack.loan.principal)} />
                <Field label="Interest Rate" value={`${pack.loan.interestRate}%`} />
                <Field
                  label="Default Rate"
                  value={pack.loan.defaultRate ? `${pack.loan.defaultRate}%` : '—'}
                />
                <Field label="Term" value={`${pack.loan.termMonths} months`} />
                <Field label="Frequency" value={pack.loan.paymentFrequency} />
                <Field label="Application Date" value={fmtDateOnly(pack.loan.applicationDate)} />
                <Field label="Settlement Date" value={fmtDateOnly(pack.loan.settlementDate)} />
                <Field label="Approval Date" value={fmtDate(pack.loan.approvalDate)} />
                <Field label="Maturity Date" value={fmtDateOnly(pack.loan.maturityDate)} />
              </div>
            </div>

            {/* Borrower */}
            {pack.borrower && (
              <div>
                <h2
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '4px',
                  }}
                >
                  2. Borrower
                </h2>
                <div className="grid grid-cols-4 gap-3 mt-2">
                  <Field label="Name" value={pack.borrower.name} />
                  <Field label="Email" value={pack.borrower.email || '—'} />
                  <Field label="Phone" value={pack.borrower.phone || '—'} />
                  <Field label="ABN/TFN" value={pack.borrower.taxId || '—'} />
                </div>
              </div>
            )}

            {/* Referrer */}
            {pack.referrer && (
              <div>
                <h2
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '4px',
                  }}
                >
                  3. Referrer
                </h2>
                <div className="grid grid-cols-4 gap-3 mt-2">
                  <Field label="Name" value={pack.referrer.name} />
                  <Field label="Company" value={pack.referrer.company || '—'} />
                  <Field label="Type" value={pack.referrer.type} />
                  <Field
                    label="Commission Rate"
                    value={pack.referrer.commissionRate ? `${pack.referrer.commissionRate}%` : '—'}
                  />
                </div>
              </div>
            )}

            {/* Fees */}
            <div>
              <h2
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '4px',
                }}
              >
                {pack.referrer ? '4' : '3'}. Fees & Costs
              </h2>
              <table className="w-full border-collapse text-xs mt-2">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-2 py-1.5 text-left">Fee</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">Total</th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">
                      Lender Retains
                    </th>
                    <th className="border border-gray-200 px-2 py-1.5 text-right">Paid Out</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-2 py-1">Establishment Fee</td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {fmt(pack.fees.establishmentFee)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right text-indigo-700">
                      {fmt(pack.fees.establishmentFeeRetained)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {fmt(pack.fees.establishmentFee - pack.fees.establishmentFeeRetained)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-2 py-1">Brokerage Fee</td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {fmt(pack.fees.brokerageFee)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right text-indigo-700">
                      {fmt(pack.fees.brokerageFeeRetained)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {fmt(pack.fees.brokerageFee - pack.fees.brokerageFeeRetained)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-2 py-1">Legal Costs</td>
                    <td className="border border-gray-200 px-2 py-1 text-right">
                      {fmt(pack.fees.legalCosts)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right text-indigo-700">
                      {fmt(pack.fees.legalCosts)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right">—</td>
                  </tr>
                  {pack.fees.otherCosts > 0 && (
                    <tr>
                      <td className="border border-gray-200 px-2 py-1">
                        {pack.fees.otherCostsDescription || 'Other'}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {fmt(pack.fees.otherCosts)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-indigo-700">
                        {fmt(pack.fees.otherCosts)}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">—</td>
                    </tr>
                  )}
                  {pack.fees.referrerCommission > 0 && (
                    <tr>
                      <td className="border border-gray-200 px-2 py-1">Referrer Commission</td>
                      <td className="border border-gray-200 px-2 py-1 text-right">—</td>
                      <td className="border border-gray-200 px-2 py-1 text-right">—</td>
                      <td className="border border-gray-200 px-2 py-1 text-right text-red-600">
                        {fmt(pack.fees.referrerCommission)}
                      </td>
                    </tr>
                  )}
                  <tr className="font-semibold bg-gray-50">
                    <td className="border border-gray-200 px-2 py-1.5">TOTAL</td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right">
                      {fmt(pack.fees.totalFees)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right text-indigo-700">
                      {fmt(pack.fees.lenderRetained)}
                    </td>
                    <td className="border border-gray-200 px-2 py-1.5 text-right text-red-600">
                      {fmt(pack.fees.paidOut)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Security */}
            <div>
              <h2
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '4px',
                }}
              >
                Security Schedule
              </h2>
              {pack.security.items.length > 0 ? (
                <>
                  <table className="w-full border-collapse text-xs mt-2">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-200 px-2 py-1.5 text-left">#</th>
                        <th className="border border-gray-200 px-2 py-1.5 text-left">
                          Description
                        </th>
                        <th className="border border-gray-200 px-2 py-1.5 text-left">Type</th>
                        <th className="border border-gray-200 px-2 py-1.5 text-right">
                          Best Estimate
                        </th>
                        <th className="border border-gray-200 px-2 py-1.5 text-right">
                          Prior Debt
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pack.security.items.map((s: any, i: number) => (
                        <tr key={i}>
                          <td className="border border-gray-200 px-2 py-1">{s.priority}</td>
                          <td className="border border-gray-200 px-2 py-1">{s.description}</td>
                          <td className="border border-gray-200 px-2 py-1">{s.type}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right">
                            {fmt(s.bestEstimate)}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right">
                            {s.existingDebt > 0 ? fmt(s.existingDebt) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="grid grid-cols-4 gap-3 mt-3">
                    <Field
                      label="Total Security Value"
                      value={fmt(pack.security.totalSecurityValue)}
                    />
                    <Field
                      label="Total LTV"
                      value={pack.security.totalLtv !== null ? `${pack.security.totalLtv}%` : '—'}
                    />
                    <Field
                      label="Our LTV"
                      value={pack.security.ourLtv !== null ? `${pack.security.ourLtv}%` : '—'}
                    />
                    <Field label="Security Cover" value={`${pack.security.securityCover}x`} />
                  </div>
                </>
              ) : (
                <p className="text-gray-400 text-xs mt-2">No security items registered</p>
              )}
            </div>

            {/* Deal Return */}
            <div>
              <h2
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '4px',
                }}
              >
                Deal Return Analysis
              </h2>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <Field
                  label="Total Interest Income"
                  value={fmt(pack.dealReturn.totalInterestIncome)}
                />
                <Field label="Total Lender Return" value={fmt(pack.dealReturn.totalLenderReturn)} />
                <Field
                  label="Return on Principal"
                  value={`${pack.dealReturn.returnOnPrincipal}%`}
                />
                <Field label="Annualised Return" value={`${pack.dealReturn.annualisedReturn}%`} />
              </div>
            </div>

            {/* Extensions */}
            {pack.extensions && pack.extensions.length > 0 && (
              <div>
                <h2
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '4px',
                  }}
                >
                  Term Extensions ({pack.extensions.length})
                </h2>
                <table className="w-full border-collapse text-xs mt-2">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-200 px-2 py-1.5 text-left">#</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-left">Date</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">Added</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">New Term</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">Fee</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">
                        Lender Keeps
                      </th>
                      <th className="border border-gray-200 px-2 py-1.5 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pack.extensions.map((ext: any) => (
                      <tr key={ext.extensionNumber}>
                        <td className="border border-gray-200 px-2 py-1">{ext.extensionNumber}</td>
                        <td className="border border-gray-200 px-2 py-1">
                          {fmtDate(ext.createdAt)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          +{ext.additionalMonths}mo
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {ext.newTermMonths}mo
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {fmt(ext.extensionFee)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right font-medium">
                          {fmt(ext.extensionFeeRetained)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-gray-500">
                          {ext.reason ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Schedule Preview */}
            {pack.schedule.length > 0 && (
              <div>
                <h2
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '4px',
                  }}
                >
                  Repayment Schedule (First 12 Periods)
                </h2>
                <table className="w-full border-collapse text-xs mt-2">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-2 py-1.5 text-left">#</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-left">Due Date</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">Principal</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">Interest</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-right">Total</th>
                      <th className="border border-gray-200 px-2 py-1.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pack.schedule.map((s: any) => (
                      <tr key={s.periodNumber}>
                        <td className="border border-gray-200 px-2 py-1">{s.periodNumber}</td>
                        <td className="border border-gray-200 px-2 py-1">
                          {fmtDateOnly(s.dueDate)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {fmt(s.principalDue)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right">
                          {fmt(s.interestDue)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1 text-right font-medium">
                          {fmt(s.totalDue)}
                        </td>
                        <td className="border border-gray-200 px-2 py-1">{s.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Credit Decisions */}
            {pack.creditDecisions.length > 0 && (
              <div>
                <h2
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '4px',
                  }}
                >
                  Credit Decisions
                </h2>
                {pack.creditDecisions.map((d: any, i: number) => (
                  <div key={i} className="border border-gray-200 rounded p-2.5 mt-2 text-xs">
                    <div className="flex items-center gap-3 mb-1">
                      <span
                        className={`font-bold ${
                          d.decision === 'APPROVED'
                            ? 'text-green-700'
                            : d.decision === 'DECLINED'
                              ? 'text-red-700'
                              : 'text-amber-700'
                        }`}
                      >
                        {d.decision}
                      </span>
                      <span className="text-gray-400">by {d.decidedBy}</span>
                      <span className="text-gray-400">{fmtDate(d.createdAt)}</span>
                    </div>
                    {d.reason && <p className="text-gray-600">{d.reason}</p>}
                    {d.conditions && (
                      <p className="text-gray-600 mt-0.5">Conditions: {d.conditions}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Team */}
            {pack.team.length > 0 && (
              <div>
                <h2
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '4px',
                  }}
                >
                  Team
                </h2>
                <div className="flex gap-3 mt-2">
                  {pack.team.map((t: any, i: number) => (
                    <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {t.name} <span className="text-gray-400">({t.role.toLowerCase()})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </>
    </ModalContentWrapper>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p
        style={{
          fontSize: '9px',
          color: '#888',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: '11px', fontWeight: 600 }}>{value}</p>
    </div>
  );
}
